// Operators' comments on an app, opened from the Apps row. Edits never lose
// the old text: the server keeps every previous version and a deleted comment
// stays in the list, struck through, with its history.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { addAppComment, deleteAppComment, editAppComment, getAppComments, type AppComment } from '@/api/apps';
import { formatDateTime } from '@/lib/datetime';

interface Props {
  app: { id: string; name: string } | null;
  canWrite: boolean;
  onClose: () => void;
}

const when = (ts: number | null) => (ts ? formatDateTime(new Date(ts * 1000).toISOString()) : '—');

function CommentItem({ appId, comment, canWrite }: { appId: string; comment: AppComment; canWrite: boolean }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [showHistory, setShowHistory] = useState(false);
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['app-comments', appId] });
    queryClient.invalidateQueries({ queryKey: ['apps'] });
  };
  const save = useMutation({
    mutationFn: () => editAppComment(appId, comment.id, draft.trim()),
    onSuccess: () => { setEditing(false); refresh(); },
  });
  const remove = useMutation({
    mutationFn: () => deleteAppComment(appId, comment.id),
    onSuccess: refresh,
  });
  const deleted = !!comment.deleted_at;
  const error = save.error ?? remove.error;

  return (
    <li className="py-3 border-b border-border last:border-b-0">
      <div className="flex items-center justify-between gap-3 text-xs text-content-subtle">
        <span>
          <span className="font-medium text-content-muted">{comment.author_name || 'unknown'}</span>
          {' · '}{when(comment.created_at)}
          {comment.updated_at && !deleted && (
            <span title={`Edited by ${comment.updated_by_name || 'unknown'}`}> · edited {when(comment.updated_at)}</span>
          )}
          {deleted && <span className="text-danger"> · deleted by {comment.deleted_by_name || 'unknown'} {when(comment.deleted_at)}</span>}
        </span>
        {!editing && (
          <span className="flex items-center gap-3 shrink-0">
            {comment.versions.length > 0 && (
              <button type="button" onClick={() => setShowHistory((v) => !v)} className="text-primary hover:underline">
                {showHistory ? 'Hide history' : `History (${comment.versions.length})`}
              </button>
            )}
            {canWrite && comment.can_edit && (
              <>
                <button type="button" onClick={() => { setDraft(comment.body); setEditing(true); }} className="text-primary hover:underline">Edit</button>
                <button
                  type="button"
                  disabled={remove.isPending}
                  onClick={() => { if (window.confirm('Delete this comment? Its text and history stay visible as deleted.')) remove.mutate(); }}
                  className="text-danger hover:underline disabled:opacity-50"
                >
                  Delete
                </button>
              </>
            )}
          </span>
        )}
      </div>

      {editing ? (
        <div className="mt-2 space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            maxLength={4000}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-content"
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
            <Button size="sm" loading={save.isPending} disabled={!draft.trim() || draft.trim() === comment.body} onClick={() => save.mutate()}>
              Save
            </Button>
          </div>
        </div>
      ) : (
        <p className={`mt-1 text-sm whitespace-pre-wrap break-words ${deleted ? 'line-through text-content-subtle' : 'text-content'}`}>{comment.body}</p>
      )}

      {showHistory && (
        // Oldest first, then the current text, so the list reads as a timeline.
        <ol className="mt-2 space-y-2 border-l-2 border-border pl-3">
          {comment.versions.map((v, i) => (
            <li key={i} className="text-xs">
              <div className="text-content-subtle">
                {i === 0 ? `Original · ${when(comment.created_at)}` : `Version ${i + 1}`}
                {' → replaced by '}{v.edited_by_name || 'unknown'} {when(v.edited_at)}
              </div>
              <p className="mt-0.5 whitespace-pre-wrap break-words text-content-muted">{v.body}</p>
            </li>
          ))}
        </ol>
      )}
      {error && <p className="mt-1 text-xs text-danger">{error instanceof Error ? error.message : 'Failed'}</p>}
    </li>
  );
}

export function AppCommentsModal({ app, canWrite, onClose }: Props) {
  const queryClient = useQueryClient();
  const appId = app?.id ?? '';
  const [text, setText] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  useEffect(() => { setText(''); setShowDeleted(false); }, [appId]);

  const comments = useQuery({ queryKey: ['app-comments', appId], queryFn: () => getAppComments(appId), enabled: !!appId });
  const add = useMutation({
    mutationFn: () => addAppComment(appId, text.trim()),
    onSuccess: () => {
      setText('');
      queryClient.invalidateQueries({ queryKey: ['app-comments', appId] });
      queryClient.invalidateQueries({ queryKey: ['apps'] });
    },
  });

  const all = comments.data ?? [];
  const deletedCount = all.filter((c) => c.deleted_at).length;
  const visible = showDeleted ? all : all.filter((c) => !c.deleted_at);

  return (
    <Modal isOpen={!!app} onClose={onClose} title={`Comments — ${app?.name ?? ''}`} size="lg">
      {canWrite && (
        <div className="space-y-2 pb-3 border-b border-border">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && text.trim()) add.mutate(); }}
            rows={3}
            maxLength={4000}
            placeholder="Add a comment…"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-content"
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-content-subtle">Ctrl+Enter to send</span>
            <Button size="sm" loading={add.isPending} disabled={!text.trim()} onClick={() => add.mutate()}>Comment</Button>
          </div>
          {add.error && <p className="text-xs text-danger">{add.error instanceof Error ? add.error.message : 'Failed'}</p>}
        </div>
      )}

      {comments.isLoading ? (
        <p className="py-6 text-center text-sm text-content-subtle">Loading…</p>
      ) : comments.error ? (
        <p className="py-6 text-center text-sm text-danger">{comments.error instanceof Error ? comments.error.message : 'Failed to load comments'}</p>
      ) : visible.length === 0 ? (
        <p className="py-6 text-center text-sm text-content-subtle">No comments yet.</p>
      ) : (
        <ul>
          {visible.map((c) => <CommentItem key={c.id} appId={appId} comment={c} canWrite={canWrite} />)}
        </ul>
      )}

      {deletedCount > 0 && (
        <button type="button" onClick={() => setShowDeleted((v) => !v)} className="mt-2 text-xs text-primary hover:underline">
          {showDeleted ? 'Hide deleted' : `Show deleted (${deletedCount})`}
        </button>
      )}
    </Modal>
  );
}
